import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Site, from, is } from '@the-real-insight/tri-model';

import { cn } from '@/lib/utils';
import { userAPI } from '../UserAPI';
import { useSelectedProject } from '../cbos/selectedProject';
import { buildingsForSite } from '../utilities/buildingLookup';
import { ringLonLatFromGeoSource } from '../utilities/normalizeGeoShape';
import { sectionHint } from './optionStyles';

/**
 * The chosen project's properties, on the map.
 *
 * The choice itself is made in the CBOS sidebar and lives in the address; this section used to
 * offer its own list, which meant the heatmap and the dashboard could be looking at different
 * projects at the same time.
 *
 * A project holds site ids and a site *may* embed its buildings — but often does not, and then the
 * building is found from the site's address, which is how the project's own Liegenschaften tab
 * arrives at its list. Skipping that second path is why this section first showed nothing for a
 * project with thirty properties. Both paths are taken here so the page only ever deals with the
 * resulting markers.
 */

export interface ProjectLocation {
    id: string;
    label: string;
    lat: number;
    lon: number;
    /** Carried so a click on the marker can turn the property back into an area. */
    street?: string;
    streetNumber?: string;
    postalCode?: string;
    city?: string;
}

interface Properties {
    selectedProjectId: string | null;
    onLocations: (locations: ProjectLocation[]) => void;
}

const idAsString = (id: any): string | undefined => {
    if (id == null) return undefined;
    if (typeof id === 'string') return id;
    if (typeof id === 'object' && '$oid' in id) return id.$oid;
    if (typeof id === 'object' && '_id' in id) return idAsString(id._id);
    return String(id);
};

/**
 * What a property is called on the map: its address.
 *
 * "Ludwigstraße 8, 06110 Halle (Saale)" — street and number first, because that
 * is what distinguishes two properties a reader is looking at, and the town after
 * it for the case where a project spans several.
 *
 * Without an address, the site's own name; and only then the building's, and only
 * then an identifier. An OSM number tells a reader nothing — but an empty tooltip
 * tells them less, and a marker has to be callable by something.
 */
const addressLabel = (
    address: { street?: string; streetNumber?: string; postalCode?: string; city?: string },
    site: any,
    building: any,
): string => {
    const line = [address.street, address.streetNumber].filter(Boolean).join(' ');
    const place = [address.postalCode, address.city].filter(Boolean).join(' ');
    const full = [line, place].filter(Boolean).join(', ');

    return full || site?.name || building?.name || building?.id_osm || '';
};

/**
 * Where to draw a building, in descending order of directness.
 *
 * `centerLat`/`centerLon` are written onto the documents by a server-side batch and are not part of
 * the model, so they may be absent; then the midpoint of the bounds, and failing that the middle of
 * the footprint. A building with none of the three is left off the map rather than placed at (0, 0).
 */
const buildingPoint = (building: any): { lat: number; lon: number } | null => {
    if (Number.isFinite(building?.centerLat) && Number.isFinite(building?.centerLon)) {
        return { lat: building.centerLat, lon: building.centerLon };
    }

    if (
        Number.isFinite(building?.minLat) &&
        Number.isFinite(building?.maxLat) &&
        Number.isFinite(building?.minLon) &&
        Number.isFinite(building?.maxLon)
    ) {
        return {
            lat: (building.minLat + building.maxLat) / 2,
            lon: (building.minLon + building.maxLon) / 2,
        };
    }

    const ring = ringLonLatFromGeoSource(building);
    if (ring && ring.length >= 3) {
        const lons = ring.map((coordinate) => coordinate[0]);
        const lats = ring.map((coordinate) => coordinate[1]);
        return {
            lat: (Math.min(...lats) + Math.max(...lats)) / 2,
            lon: (Math.min(...lons) + Math.max(...lons)) / 2,
        };
    }

    return null;
};

export default function ProjectSection({ selectedProjectId, onLocations }: Properties) {
    const { t } = useTranslation();
    const { projects, loading: loadingProjects } = useSelectedProject();
    const [loadingLocations, setLoadingLocations] = useState(false);
    // Null until a project has been resolved once — so "none found" is not claimed before looking.
    const [locationCount, setLocationCount] = useState<number | null>(null);

    useEffect(() => {
        if (!selectedProjectId) {
            onLocations([]);
            setLocationCount(null);
            return;
        }

        const project = projects.find((entry) => idAsString(entry._id) === selectedProjectId);
        if (!project) return;

        let cancelled = false;
        setLoadingLocations(true);

        const load = async () => {
            const siteIds: string[] = (project.sites ?? [])
                .map((site: any) => idAsString(site))
                .filter((id: string | undefined): id is string => !!id);

            const sites = (
                await Promise.all(
                    siteIds.map((siteId) =>
                        userAPI
                            .queryOne(from(Site.type).where(is('_id').equal(siteId)))
                            .catch((error: any) => {
                                // Logged, not swallowed: a site that cannot be read is a missing marker,
                                // and silence here is what made the empty map impossible to explain.
                                console.error('Could not read a project site', siteId, error);
                                return null;
                            }),
                    ),
                )
            ).filter(Boolean);

            /**
             * Die Gebäude je Liegenschaft — über `buildingsForSite`, nicht von Hand.
             *
             * Hier stand eine eigene Fassung davon, und sie ging von einer falschen Annahme aus:
             * dass eine Liegenschaft ihre Gebäude *eingebettet* trägt. Live steht dort eine Liste
             * von Kennungen — `linkBuildingToSite` schreibt sie so, und die bestehenden
             * Liegenschaften halten es ebenso. Eine Kennung ist kein Gebäude: Sie hat keine
             * Koordinaten, fiel bei der Verortung heraus, und weil die Liste nicht leer war, kam
             * die Auflösung über die Adresse auch nicht zum Zug. Ergebnis war eine Karte, die
             * „keine verorteten Liegenschaften" meldete, während zwei hinterlegt waren.
             *
             * `buildingsForSite` kennt beide Fälle und die richtige Reihenfolge: erst die
             * eingetragenen Kennungen nachladen, und nur wo nichts eingetragen ist, über die
             * Adresse suchen — eine absichtlich geschriebene Verknüpfung wird nicht bei jedem Laden
             * verworfen.
             *
             * Die Liegenschaft bleibt neben ihrem Gebäude stehen, weil die Anschrift *ihr* gehört:
             * Ein `Building` hat weder Straße noch Ort, nur eine wahlweise eingebettete Adresse,
             * die eine aus OSM übernommene Grundfläche nicht mitbringt.
             */
            const pairs = (
                await Promise.all(
                    sites.map(async (site: any) => {
                        const found = await buildingsForSite(site).catch((error: any) => {
                            console.error('Could not resolve the buildings of a site', site?._id, error);
                            return [] as any[];
                        });

                        return found.map((building: any) => ({ site, building }));
                    }),
                )
            ).flat();

            const byId = new Map<string, { site: any; building: any }>();
            pairs.forEach((pair) => {
                const id = idAsString(pair.building?._id ?? pair.building?.id);
                if (id && !byId.has(id)) byId.set(id, pair);
            });

            const locations = Array.from(byId.entries())
                .map(([id, { site, building }]): ProjectLocation | null => {
                    const point = buildingPoint(building);
                    if (!point) return null;

                    // The site first, then whatever address the building happens to carry.
                    const street = site.street ?? building.address?.street;
                    const streetNumber = site.streetNumber ?? building.address?.streetNumber;
                    const postalCode = site.postalCode ?? building.address?.postalCode;
                    const city = site.city ?? building.address?.city;

                    return {
                        id,
                        label: addressLabel({ street, streetNumber, postalCode, city }, site, building),
                        lat: point.lat,
                        lon: point.lon,
                        street,
                        streetNumber,
                        postalCode,
                        city,
                    };
                })
                .filter((location): location is ProjectLocation => location !== null);

            if (!cancelled) {
                onLocations(locations);
                setLocationCount(locations.length);
            }
        };

        load()
            .catch((error: any) => console.error('Could not load the project locations', error))
            .finally(() => {
                if (!cancelled) setLoadingLocations(false);
            });

        return () => {
            cancelled = true;
        };
        // `onLocations` is a fresh closure on every render of the page; depending on it would
        // reload the project on every keystroke elsewhere in the chimney.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProjectId, projects]);

    // Nothing to say, nothing on screen. The project's name is in the page title and its properties
    // are on the map; a card repeating the name would be a third place to read the same word.
    if (loadingProjects) return <></>;

    if (projects.length === 0) return <Hint>{t('heatmap.project.none')}</Hint>;

    if (!selectedProjectId) return <Hint>{t('heatmap.project.chooseInSidebar')}</Hint>;

    if (loadingLocations) {
        return (
            <Hint>
                <Loader2
                    className="mr-2 inline h-3.5 w-3.5 animate-spin align-[-2px]"
                    role="status"
                    aria-label={t('global.loading')}
                />
                {t('global.loading')} …
            </Hint>
        );
    }

    // A project whose properties cannot be placed would otherwise just leave the map empty, which
    // reads like a broken page rather than like missing coordinates.
    if (locationCount === 0) return <Hint>{t('heatmap.project.noLocations')}</Hint>;

    return <></>;
}

/** The section's only remaining output: a line, and only when something is amiss. */
function Hint({ children }: { children: any }) {
    return <div className={cn(sectionHint, 'px-1')}>{children}</div>;
}
